import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Switch,
  Stack,
  Typography,
  Divider,
} from '@mui/material'
import {
  Search as SearchIcon,
  Edit as EditIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  ArchiveOutlined as ArchiveOutlinedIcon,
  UnarchiveOutlined as UnarchiveOutlinedIcon,
  SettingsBackupRestore as RestoreIcon,
  LockClock as RetireIcon,
  FileDownloadOutlined as ExportIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'
import { showModal } from 'actions/app'
import { get, noop, isEmpty } from 'es-toolkit/compat'
import FieldForm from 'components/field-form'
import type { FieldModel } from 'store/state/field'

import {
  DataGrid,
  type GridRowId,
  type GridColDef,
  type GridRowSelectionModel,
  type GridComparatorFn,
} from '@mui/x-data-grid'
import { useCallback, useEffect, useState, useRef } from 'react'
import type { AppState } from 'state/index'
import { unitConvert } from '@farmtrx/units'
import EditMultipleFields from './edit-multiple-fields'
import OCFFDeleteField from './delete-field'
import ArchiveField from './archive-field'
import RestoreFields from './restore-fields'
import ReactivateFields from './reactivate-fields'
import RetireFields from './retire-field'
import { fetchFieldsPaginated, exportFieldBoundary } from 'api/field'
import colors from 'common/colors'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TEST_IDS } from '../../../test-ids'

interface FieldResult extends FieldModel {
  farmName?: string
  clientName?: string
  clientId?: number
}

interface Farm {
  id: string | number
  name: string
  clientId: string | number
}

interface Client {
  id: string | number
  name: string
}

interface UserPrefs {
  units: {
    area_in: string
  }
}

interface FarmDataMapEntry {
  name: string
  clientId: string | number
  clientName?: string
}

interface FieldListEntry extends FieldModel {
  farmName?: string
  clientId: string | number
  clientName?: string
  fieldArea: string
  boundarySvg: string
}

interface RootState {
  field?: { collection: FieldModel[] }
  farm?: { collection: Farm[] }
  client?: { collection: Client[] }
  account: {
    singleton: { prefs: UserPrefs }
    permissions: string[]
  }
}

export default function OCFFieldsComponent() {
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({
    farmName: true,
    fieldName: true,
    clientName: true,
  })
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const farms = useSelector((state: RootState) => state?.farm?.collection ?? [])
  const fields = useSelector(
    (state: RootState) => state?.field?.collection ?? [],
  )
  const clients = useSelector(
    (state: RootState) => state?.client?.collection ?? [],
  )
  const [farmDataMap, setFarmDataMap] = useState<
    Record<string | number, FarmDataMapEntry>
  >({})
  const [fieldList, setFieldList] = useState<FieldListEntry[]>([])
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [canRetire, setCanRetire] = useState(false)
  const [canReactivate, setCanReactivate] = useState(false)
  const userPrefs = useSelector(
    (state: RootState) => state.account.singleton.prefs,
  )
  const fieldAreaSort: GridComparatorFn<string> = (v1, v2) => {
    const area1 = parseFloat(v1.split(' ')[0]) || 0
    const area2 = parseFloat(v2.split(' ')[0]) || 0
    return area1 - area2
  }
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  const [searchValue, setSearchValue] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const permissions = useSelector((state) =>
    get(state as AppState, 'account.permissions'),
  )

  const [showDeletedFields, setShowDeletedFields] = useState(false)
  const showFields = permissions.includes('farm:read')
  const addModifyFields = permissions.includes('farm:write')

  const [columns, setColumns] = useState<GridColDef[]>([])

  useEffect(() => {
    const _farmMap: Record<string | number, FarmDataMapEntry> = {}

    for (let i = 0; i < farms.length; i++) {
      const farm = farms[i]
      const client = clients.find((c) => c.id === farm.clientId)
      _farmMap[farm.id] = {
        name: farm.name,
        clientId: farm.clientId,
        clientName: client?.name,
      }
    }
    setFarmDataMap(_farmMap)
  }, [farms, clients])

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    }
  }, [searchValue])

  function fetchData(
    page: number,
    pageSize: number,
    includeSvgs: boolean,
    search: string,
    showDeleted: string,
    ignore: boolean,
  ) {
    fetchFieldsPaginated(page, pageSize, includeSvgs, search, showDeleted)
      .then((res) => {
        if (!ignore) {
          const { results, pagination } = res.data
          const _fieldList = (results || []).map((field: FieldResult) => ({
            id: field.id,
            name: field.name,
            farmId: field.farmId,
            farmName: field.farmName,
            clientId: field.clientId,
            clientName: field.clientName,
            fieldArea: `${unitConvert
              .convertAreaToPref(field.areaSqm, userPrefs['units']['area_in'])
              .toFixed(2)} ${userPrefs['units']['area_in']}`,
            boundarySvg: field.boundarySvg,
            deletedAt: field.deletedAt,
            retiredAt: field.retiredAt,
          }))
          setFieldList(_fieldList)
          setRowCount(pagination?.total || 0)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    let ignore = false
    setLoading(true)
    if (showFields) {
      fetchData(
        (paginationModel.page || 0) + 1,
        paginationModel.pageSize,
        true,
        debouncedSearch,
        //@ts-expect-error
        showDeletedFields,
        ignore,
      )
    } else {
      setFieldList([])
      setRowCount(0)
      setLoading(false)
    }
    return () => {
      ignore = true
    }
  }, [
    paginationModel,
    debouncedSearch,
    showFields,
    userPrefs,
    showDeletedFields,
  ])

  // Filtering is now handled server-side

  const onCreateField = useCallback(() => {
    dispatch(showModal(<FieldForm callback={updateFieldList} />))
  }, [dispatch])

  const onEditField = useCallback(() => {
    if (rowSelectionModel.ids.size === 1) {
      const fieldToEdit = fieldList.find(
        (f) => f.id === rowSelectionModel.ids.values().next().value,
      )
      if (!fieldToEdit) return
      dispatch(
        showModal(
          <FieldForm
            defaultClient={farmDataMap[fieldToEdit.farmId]?.clientId}
            defaultFarm={fieldToEdit.farmId}
            fieldData={fieldToEdit}
            callback={updateFieldList}
          />,
        ),
      )
    } else {
      const fieldIds = Array.from(rowSelectionModel.ids)
      const fieldsToEdit: {
        id: string | number
        name: string
        farmId: string | number
      }[] = []
      for (let i = 0; i < fieldIds.length; i++) {
        const foundField = fieldList.find((f) => f.id == fieldIds[i])
        if (foundField) {
          fieldsToEdit.push({
            id: foundField.id,
            name: foundField.name,
            farmId: foundField.farmId,
          })
        }
      }
      dispatch(
        showModal(
          <EditMultipleFields
            fields={fieldsToEdit}
            callback={updateFieldList}
          />,
        ),
      )
    }
  }, [rowSelectionModel, fieldList, farmDataMap, dispatch])

  const onExportField = useCallback(() => {
    if (rowSelectionModel.ids.size >= 1) {
      const fieldIds = Array.from(rowSelectionModel.ids.values())
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id))

      if (fieldIds.length === 0) return

      exportFieldBoundary(fieldIds)
        .then((res) => {
          const blob = new Blob([res.data], { type: 'application/zip' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url

          // Build filename from selected field names
          const selectedFields = fieldList.filter((f) =>
            fieldIds.includes(f.id),
          )
          const fieldNames = selectedFields.map((f) => f.name).join('_')
          const safeFieldNames = fieldNames
            .replace(/[^a-zA-Z0-9._-]/g, '_')
            .slice(0, 150)
          const fileName = safeFieldNames
            ? `${safeFieldNames}_boundary.zip`
            : 'fields_boundary.zip'

          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error('Failed to export field boundary:', error)
        })
    }
  }, [rowSelectionModel, fieldList])

  useEffect(() => {
    const _columns: GridColDef[] = [
      {
        field: 'boundarySvg',
        headerName: '',
        minWidth: 50,
        maxWidth: 50,
        flex: 1,
        renderCell: (params) => {
          if (params.value !== '') {
            const svg = new Blob([params.value], { type: 'image/svg+xml' })
            const url = URL.createObjectURL(svg)
            return (
              <div>
                <img src={url} height="40" width="40" />
              </div>
            )
          } else {
            return <div />
          }
        },
      },
      {
        field: 'name',
        headerName: t('field'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        headerClassName: 'data-grid--header',
        renderCell: (params) => {
          const fontStyle = params.row?.retiredAt ? 'italic' : 'normal'
          return <div style={{ fontStyle }}>{params.value}</div>
        },
      },
      {
        field: 'farmName',
        headerName: t('farm'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        headerClassName: 'data-grid--header',
        renderCell: (params) => {
          const fontStyle = params.row?.retiredAt ? 'italic' : 'normal'
          return <div style={{ fontStyle }}>{params.value}</div>
        },
      },
      {
        field: 'clientName',
        headerName: t('client'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        headerClassName: 'data-grid--header',
        renderCell: (params) => {
          const fontStyle = params.row?.retiredAt ? 'italic' : 'normal'
          return <div style={{ fontStyle }}>{params.value}</div>
        },
      },
      {
        field: 'fieldArea',
        headerName: t('field_area'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        headerClassName: 'data-grid--header',
        sortComparator: fieldAreaSort,
        renderCell: (params) => {
          const fontStyle = params.row?.retiredAt ? 'italic' : 'normal'
          return <div style={{ fontStyle }}>{params.value}</div>
        },
      },
      {
        field: 'retiredAt',
        headerName: t('retirement_year'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        headerClassName: 'data-grid--header',
        renderCell: (params) => {
          let displayValue = ''
          if (params.value) {
            displayValue = params.value.split('-')[0]
          }
          const fontStyle = params.row?.retiredAt ? 'italic' : 'normal'
          return <div style={{ fontStyle }}>{displayValue}</div>
        },
      },
      {
        field: 'deletedAt',
        headerName: t('status'),
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        headerClassName: 'data-grid--header',
        renderCell: (params) => {
          const statusText = params.value
            ? t('archived')
            : params.row?.retiredAt
              ? t('retired')
              : t('active')

          const fontStyle = params.row?.retiredAt ? 'italic' : 'normal'
          return <div style={{ fontStyle }}>{statusText}</div>
        },
      },
    ]
    setColumns(_columns)
  }, [t])

  function CustomNoRowsOverlayFields() {
    if (showFields) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            marginLeft: '2rem',
            marginRight: '2rem',
          }}
        >
          {t('no_fields_to_display')}
        </div>
      )
    } else
      return (
        <div style={{ textAlign: 'center', padding: '32px' }}>
          {t('no_permissions', { item: t('fields') })}
        </div>
      )
  }

  const deleteField = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedField = fieldList.find(
        (f) => f.id === rowSelectionModel.ids.values().next().value,
      )
      if (selectedField) {
        dispatch(
          showModal(
            <OCFFDeleteField
              selectedField={selectedField}
              selectedFieldIds={Array.from(rowSelectionModel.ids)}
              callback={updateFieldList}
            />,
          ),
        )
      }
    }
  }, [rowSelectionModel, fieldList, dispatch])

  const archiveField = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedField = fieldList.find(
        (f) => f.id === rowSelectionModel.ids.values().next().value,
      )
      if (selectedField) {
        dispatch(
          showModal(
            <ArchiveField
              selectedFieldIds={Array.from(rowSelectionModel.ids)}
              callback={updateFieldList}
            />,
          ),
        )
      }
    }
  }, [rowSelectionModel, fieldList, dispatch])

  useEffect(() => {
    if (rowSelectionModel.ids.size === 0) {
      setCanRetire(false)
      setCanReactivate(false)
    } else {
      let _canRetire = false
      let _canReactivate = false
      for (let i = 0; i < fieldList.length; i++) {
        if (rowSelectionModel.ids.has(fieldList[i].id)) {
          if (fieldList[i].retiredAt) {
            _canReactivate = true
          } else {
            _canRetire = true
          }
        }
      }
      setCanRetire(_canRetire && !_canReactivate)
      setCanReactivate(_canReactivate && !_canRetire)
    }
  }, [rowSelectionModel, fieldList])

  const onUnarchiveField = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedField = fieldList.find(
        (f) => f.id === rowSelectionModel.ids.values().next().value,
      )
      if (selectedField) {
        dispatch(
          showModal(
            <RestoreFields
              selectedFieldIds={Array.from(rowSelectionModel.ids)}
              callback={updateFieldList}
            />,
          ),
        )
      }
    }
  }, [rowSelectionModel, fieldList, dispatch])

  const onChangeShowFields = useCallback(() => {
    setShowDeletedFields(!showDeletedFields)
  }, [showDeletedFields])

  const onReactivateField = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedField = fieldList.find(
        (f) => f.id === rowSelectionModel.ids.values().next().value,
      )
      if (selectedField) {
        dispatch(
          showModal(
            <ReactivateFields
              fields={fieldList.filter((f) => rowSelectionModel.ids.has(f.id))}
              callback={updateFieldList}
            />,
          ),
        )
      }
    }
  }, [rowSelectionModel, fieldList, dispatch])

  const onRetireField = useCallback(() => {
    if (rowSelectionModel.ids.size) {
      const selectedField = fieldList.find(
        (f) => f.id === rowSelectionModel.ids.values().next().value,
      )
      if (selectedField) {
        dispatch(
          showModal(
            <RetireFields
              fields={fieldList.filter((f) => rowSelectionModel.ids.has(f.id))}
              callback={updateFieldList}
            />,
          ),
        )
      }
    }
  }, [rowSelectionModel, fieldList, dispatch])

  const onClick = noop
  const updateFieldList = useCallback(() => {
    fetchData(
      (paginationModel.page || 0) + 1,
      paginationModel.pageSize,
      true,
      debouncedSearch,
      //@ts-expect-error
      showDeletedFields,
      false,
    )
    setRowSelectionModel({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  }, [
    paginationModel,
    debouncedSearch,
    showFields,
    userPrefs,
    showDeletedFields,
  ])
  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 4,
          width: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h1>{t('fields')}</h1>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                }}
              >
                {t('archived')}
              </Typography>
              <Switch
                size="small"
                checked={!showDeletedFields}
                onChange={onChangeShowFields}
              />
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  marginRight: '10px',
                }}
              >
                {t('active')}
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            {!showDeletedFields ? (
              <>
                <IconButton
                  style={{ display: addModifyFields ? 'inline-flex' : 'none' }}
                  disabled={rowSelectionModel.ids.size === 0}
                  onClick={archiveField}
                  title={t('archive_item', {
                    item:
                      rowSelectionModel.ids.size > 1 ? t('fields') : t('field'),
                  })}
                  size="large"
                >
                  <ArchiveOutlinedIcon
                    style={{
                      color:
                        rowSelectionModel.ids.size > 0 ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: addModifyFields ? 'inline-flex' : 'none' }}
                  onClick={onRetireField}
                  title={t('retire_field')}
                  size="large"
                  disabled={!canRetire || rowSelectionModel.ids.size === 0}
                >
                  <RetireIcon
                    style={{
                      color:
                        canRetire && rowSelectionModel.ids.size > 0
                          ? colors.brand
                          : 'gray',
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: addModifyFields ? 'inline-flex' : 'none' }}
                  title={t('edit_field')}
                  disabled={rowSelectionModel.ids.size === 0}
                  onClick={onEditField}
                  size="large"
                >
                  <EditIcon
                    style={{
                      color: rowSelectionModel.ids.size ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: showFields ? 'inline-flex' : 'none' }}
                  data-testid={TEST_IDS.fieldExportButton}
                  aria-label={t('export_field')}
                  title={t('export_field')}
                  disabled={rowSelectionModel.ids.size < 1}
                  onClick={onExportField}
                  size="large"
                >
                  <ExportIcon
                    style={{
                      color:
                        rowSelectionModel.ids.size >= 1 ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: addModifyFields ? 'inline-flex' : 'none' }}
                  onClick={onReactivateField}
                  title={t('reactivate_field')}
                  size="large"
                  disabled={!canReactivate || rowSelectionModel.ids.size === 0}
                >
                  <RestoreIcon
                    style={{
                      color:
                        canReactivate && rowSelectionModel.ids.size > 0
                          ? colors.brand
                          : 'gray',
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: addModifyFields ? 'inline-flex' : 'none' }}
                  onClick={onCreateField}
                  title={t('add_field')}
                  size="large"
                >
                  <AddIcon />
                </IconButton>
              </>
            ) : (
              <>
                <IconButton
                  style={{ display: addModifyFields ? 'inline-flex' : 'none' }}
                  disabled={rowSelectionModel.ids.size === 0}
                  onClick={deleteField}
                  title={t('delete_item', {
                    item:
                      rowSelectionModel.ids.size > 1 ? t('fields') : t('field'),
                  })}
                  size="large"
                >
                  <DeleteOutlineOutlinedIcon
                    style={{
                      color:
                        rowSelectionModel.ids.size > 0 ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
                <IconButton
                  style={{ display: addModifyFields ? 'inline-flex' : 'none' }}
                  disabled={rowSelectionModel.ids.size === 0}
                  onClick={onUnarchiveField}
                  title={t('restore_item', {
                    item:
                      rowSelectionModel.ids.size > 1 ? t('fields') : t('field'),
                  })}
                  size="large"
                >
                  <UnarchiveOutlinedIcon
                    style={{
                      color:
                        rowSelectionModel.ids.size > 0 ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
              </>
            )}
            <TextField
              id="search"
              onChange={onSearch}
              type="search"
              placeholder="Search"
              hiddenLabel
              sx={{ marginLeft: '10px' }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            overflowX: 'auto',
            minHeight: `calc(52px * ${paginationModel?.pageSize || 10} + 56px)`,

            // 52px is approx row height, 56px for header/pagination controls
            '& .data-grid--header': {
              background: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
        >
          <DataGrid
            autoHeight
            checkboxSelection
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            rows={fieldList}
            loading={loading}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
              if (selection?.type === 'exclude' && selection?.ids.size === 0) {
                // Setting to include all rows when "select all" is chosen, since DataGrid doesn't provide the list of all row IDs in this case
                // which caused some inconsistencies with the selection state. The rows showed as selected but the selection model was empty,
                // which caused issues with the archive/restore and delete actions.
                const allIds = new Set(fieldList.map((row) => row.id))
                setRowSelectionModel({ type: 'include', ids: allIds })
              } else {
                setRowSelectionModel(selection)
              }
            }}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={(model) =>
              setColumnVisibility(model)
            }
            initialState={{
              sorting: {
                sortModel: [{ field: 'fieldName', sort: 'asc' }],
              },
            }}
            slots={{
              noRowsOverlay: CustomNoRowsOverlayFields,
            }}
            columns={columns}
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: colors.greyBackground,
                textTransform: 'uppercase',
              },
            }}
            pageSizeOptions={[10, 20, 50]}
            onRowClick={(params) => onClick(params.row)}
          />
        </Box>
      </Box>
    </>
  )
}
